import { useTranslation } from 'react-i18next';
import { newsItems } from '@/mocks/newsData';

export default function NewsSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'fr' | 'en';

  const getDate = (item: typeof newsItems[0]) => (lang === 'en' ? item.dateEn : item.date);
  const getTitle = (item: typeof newsItems[0]) => (lang === 'en' ? item.titleEn : item.title);
  const getExcerpt = (item: typeof newsItems[0]) => (lang === 'en' ? item.excerptEn : item.excerpt);
  const getCategory = (item: typeof newsItems[0]) => (lang === 'en' ? item.categoryEn : item.category);
  const featured = newsItems.filter((n) => n.featured);

  return (
    <section id="news" className="py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
          <div>
            <span className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-accent-600">{t('news.title')}</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mt-2">{t('news.subtitle')}</h2>
          </div>
          <a
            href="/news"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('news.viewAll')}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </div>
          </a>
        </div>

        {/* Featured */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {featured.slice(0, 2).map((item) => (
            <a
              key={item.id}
              href={`/news#${item.id}`}
              className="group relative rounded-2xl overflow-hidden cursor-pointer block h-[320px] md:h-[380px]"
            >
              <img
                src={item.image}
                alt={getTitle(item)}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <span className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  {getCategory(item)}
                </span>
                <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-2">{getTitle(item)}</h3>
                <p className="text-sm text-background-50/70 line-clamp-2">{getExcerpt(item)}</p>
              </div>
            </a>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {newsItems.filter((n) => !n.featured).slice(0, 4).map((item) => (
            <a
              key={item.id}
              href={`/news#${item.id}`}
              className="group bg-background-100 rounded-2xl overflow-hidden cursor-pointer block border border-background-200/70 transition-all duration-300 hover:border-accent-200/50"
            >
              <div className="h-44 overflow-hidden">
                <img
                  src={item.image}
                  alt={getTitle(item)}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4">
                <span className="text-xs text-accent-600 font-semibold">{getCategory(item)}</span>
                <span className="text-xs text-foreground-400 ml-2">{getDate(item)}</span>
                <h4 className="font-heading text-base font-semibold text-foreground-950 mt-1.5 mb-1.5 line-clamp-2">{getTitle(item)}</h4>
                <p className="text-xs text-foreground-500 line-clamp-2">{getExcerpt(item)}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}